import { useState } from 'react';
import { Alert, App, Button, Form, Select, Space, type FormInstance } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { useContacts } from '../../contacts/hooks/useContacts.js';
import { useCompanies } from '../../tasks/hooks/useTaskLookups.js';
import type { EntityLinkFormValues } from '../../entity-links/useEntityLinkCascade.js';

export function DealLinkFields<T extends EntityLinkFormValues>({
  form,
  lockedContactId,
  lockedCompanyId,
  onChange,
  onConflict,
}: {
  form: FormInstance<T>;
  lockedContactId?: string;
  lockedCompanyId?: string;
  onChange: () => void;
  onConflict: (conflict: boolean) => void;
}) {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const contacts = useContacts();
  const companies = useCompanies();
  const contactId = Form.useWatch('contactId', form) as string | undefined;
  const companyId = Form.useWatch('companyId', form) as string | undefined;
  const contact = contacts.data?.find((item) => item.id === contactId);
  const [previousCompany, setPreviousCompany] = useState<{ id: string | undefined } | null>(null);
  const set = (value: EntityLinkFormValues): void => {
    form.setFieldsValue(value as Parameters<FormInstance<T>['setFieldsValue']>[0]);
    onChange();
  };
  const chooseContact = (id?: string): void => {
    const selected = contacts.data?.find((item) => item.id === id);
    const apply = (): void => {
      set({ contactId: id, companyId: lockedCompanyId ?? selected?.companyId ?? undefined });
      setPreviousCompany(null);
      onConflict(false);
    };
    if (!lockedCompanyId && companyId && selected && companyId !== selected.companyId) {
      modal.confirm({
        title: t('crm.deal_detail.replace_company'),
        okText: t('crm.entity_detail.confirm'),
        cancelText: t('crm.entity_detail.cancel'),
        onOk: apply,
        onCancel: () => set({ contactId }),
      });
    } else {
      set({
        contactId: id,
        ...(!companyId && selected?.companyId ? { companyId: selected.companyId } : {}),
      });
    }
  };
  const chooseCompany = (id?: string): void => {
    const old = companyId;
    if (id && contact && contact.companyId !== id) {
      setPreviousCompany({ id: old });
      onConflict(true);
    } else {
      setPreviousCompany(null);
      onConflict(false);
    }
    set({ companyId: id });
  };
  const companyOptions = [...(companies.data ?? [])]
    .sort((a, b) => Number(b.id === contact?.companyId) - Number(a.id === contact?.companyId))
    .filter((item) => !lockedContactId || item.id === contact?.companyId)
    .map((item) => ({ value: item.id, label: item.name }));
  return (
    <>
      {!lockedContactId ? (
        <Form.Item name="contactId" label={t('crm.deal_card.details.contact')}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={contacts.isLoading}
            onChange={chooseContact}
            options={(contacts.data ?? [])
              .filter((item) => !lockedCompanyId || item.companyId === lockedCompanyId)
              .map((item) => ({
                value: item.id,
                label: item.companyRef
                  ? `${item.fullName} · ${item.companyRef.name}`
                  : item.fullName,
              }))}
          />
        </Form.Item>
      ) : (
        <Form.Item name="contactId" hidden>
          <Select />
        </Form.Item>
      )}
      {!lockedCompanyId ? (
        <Form.Item name="companyId" label={t('crm.deal_detail.company')}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={companies.isLoading}
            onChange={chooseCompany}
            options={companyOptions}
          />
        </Form.Item>
      ) : (
        <Form.Item name="companyId" hidden>
          <Select />
        </Form.Item>
      )}
      {previousCompany ? (
        <Alert
          className="deal-form-full"
          type="warning"
          showIcon
          title={t('crm.deal_detail.company_mismatch')}
          action={
            <Space>
              <Button
                size="small"
                onClick={() => {
                  set({ contactId: undefined });
                  setPreviousCompany(null);
                  onConflict(false);
                }}
              >
                {t('crm.entity_detail.confirm')}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  set({ companyId: previousCompany.id });
                  setPreviousCompany(null);
                  onConflict(false);
                }}
              >
                {t('crm.entity_detail.undo')}
              </Button>
            </Space>
          }
        />
      ) : null}
    </>
  );
}
